'use client';

import { type ReactNode, useEffect } from 'react';
import usePrintModalExperiment from '@alltrails/experiments/hooks/usePrintModalExperiment';
import type { AllTrailsBounds } from '@alltrails/maps/types/Geo';
import PrintMapButton from '@alltrails/print/components/PrintMapButton/PrintMapButton';
import type { PrintHost } from '@alltrails/print/components/PrintModal/PrintModal';
import type { PrintSubject } from '@alltrails/print/types/PrintSubject';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import { clearPrintRequest } from '@/utils/slices/exploreSlice';
import useClearPrintRequested from './useClearPrintRequested';
import usePrintHost from './usePrintHost';
import usePrintRequested from './usePrintRequested';

type PrintControlProps = {
  /** Frames the print. Null while the route has nothing printable yet, which renders no button. */
  bounds: AllTrailsBounds | null;
  hasControlArmCounterpart?: boolean;
  mapContent?: ReactNode;
  mapLocation: PrintHost['mapLocation'];
  subject: PrintSubject;
  trailId?: number;
};

/** The print control a route renders when it has somewhere on the map to put a button. */
const PrintControl = ({ bounds, hasControlArmCounterpart = false, mapContent, mapLocation, subject, trailId }: PrintControlProps) => {
  const host = usePrintHost(mapLocation, mapContent, trailId);
  const isPrintRequestedByUrl = usePrintRequested();
  const isPrintRequestedInApp = useAppSelector(state => state.explore.isPrintRequested);
  const { sendExposure } = usePrintModalExperiment();
  const clearPrintRequestedByUrl = useClearPrintRequested();
  const dispatch = useAppDispatch();

  // Consumed as soon as it can be acted on, and dropped on the way out, so a request can never
  // outlive the surface that made it
  useEffect(() => {
    if (isPrintRequestedInApp && bounds) {
      dispatch(clearPrintRequest());
    }
  }, [bounds, dispatch, isPrintRequestedInApp]);

  useEffect(
    () => () => {
      dispatch(clearPrintRequest());
    },
    [dispatch]
  );

  return (
    <PrintMapButton
      autoOpen={isPrintRequestedByUrl || isPrintRequestedInApp}
      bounds={bounds}
      host={host}
      onClose={clearPrintRequestedByUrl}
      onPrintRequested={hasControlArmCounterpart ? sendExposure : undefined}
      subject={subject}
    />
  );
};

export default PrintControl;
